import React, { useContext, useState, useMemo } from 'react'; // Added useMemo
import NavBar from 'react-bootstrap/Navbar';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon as farMoon } from '@fortawesome/free-regular-svg-icons';
import { faSun as fasSun } from '@fortawesome/free-solid-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { faTh } from '@fortawesome/free-solid-svg-icons';
import { AppThemeContext } from '../../context/AppThemeContext';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import Accordion from 'react-bootstrap/Accordion'; // Import Accordion
import TryNewDeckBanner from './TryNewDeckBanner';
import { usePageLeaveGuard } from './PageLeaveGuardContext';
import './Header.css'; 
import changelogData, { latestVersion } from '../../utils/changelogData';
import catalogChangelogData, { catalogLatestVersion } from '../../utils/catalog/catalogChangelogData';
import { isCatalogPath } from '../../utils/promoChrome';

function Header() {
    const { theme, toggleTheme } = useContext(AppThemeContext);
    const { navigate } = usePageLeaveGuard();
    const darkMode = theme === 'dark';
    const catalogMode = isCatalogPath();
    const activeChangelog = catalogMode ? catalogChangelogData : changelogData;
    const activeLatest = catalogMode ? catalogLatestVersion : latestVersion;
    const [showChangelogModal, setShowChangelogModal] = useState(false);
    const modalThemeClass = theme === 'dark' ? 'bg-dark text-white changelog-modal-dark' : 'changelog-modal-light';

    const handleCloseChangelog = () => setShowChangelogModal(false);
    const handleShowChangelog = () => setShowChangelogModal(true);

    const openMetaPTCG = () => {
        window.open('https://meta-ptcg.org', '_blank');
    };

    // Memoize keywordsMap to prevent re-creation on every render
    const keywordsMap = useMemo(() => ({
        "ADDED:": "changelog-keyword-added",
        "IMPROVED:": "changelog-keyword-improved",
        "FIXED:": "changelog-keyword-fixed",
        "CHANGED:": "changelog-keyword-changed",
        "REMOVED:": "changelog-keyword-removed",
        "REDUCED:": "changelog-keyword-reduced",
        "REPLACED:": "changelog-keyword-replaced",
        "NEW:": "changelog-keyword-new",
        "UPDATED:": "changelog-keyword-updated",
        // Subsection titles - ensure these exactly match the start of the strings in changelogData
        "**Revamped Card Search Panel (`EnhancedCardSearchPanel.js`):**": "changelog-subsection-title tech-path",
        "**Advanced Deck Export (`EnhancedExportModal.js`):**": "changelog-subsection-title tech-path",
        "**Deck Image Generation (`ImprovedDeckImageModal.js`):**": "changelog-subsection-title tech-path",
        "**Custom Card Management (`CustomCardManager.js`, `EnhancedTCGController.js`):**": "changelog-subsection-title tech-path",
        "**Deck Persistence & Import (`DeckViewPanel.js`, `StorageManager.js`, `ImportValidator.js`):**": "changelog-subsection-title tech-path",
        "**Drag and Drop (`DeckViewPanel.js`, `CardContainer.js`):**": "changelog-subsection-title tech-path",
        "**Optimized Image Loading (`ImageOptimizer.js`, `PkmnCard.js`):**": "changelog-subsection-title tech-path",
        "**Optimized Initial Load (`EnhancedCardSearchPanel.js`):**": "changelog-subsection-title tech-path",
        "**Efficient Data Storage (`StorageManager.js`, `EnhancedTCGController.js`):**": "changelog-subsection-title tech-path",
        "**Card Preview (`EnhancedCardSearchPanel.js`, `CardPreviewModal.js`):**": "changelog-subsection-title tech-path",
        "**Search Functionality (`EnhancedCardSearchPanel.js`):**": "changelog-subsection-title tech-path",
        "**General UI & Logic:**": "changelog-subsection-title",
        "**Modular Design:**": "changelog-subsection-title",
        "**Controller Logic (`EnhancedTCGController.js`, `TCGController.js`):**": "changelog-subsection-title tech-path",
        "**Component Updates (`PkmnCard.js`, `CardContainer.js`, `CustomSetTabs.js`, etc.):**": "changelog-subsection-title tech-path",
        "**CSS Overhaul (`CardSearchPanel.module.css`):**": "changelog-subsection-title tech-path",
        "**Custom Card Creation System:**": "changelog-subsection-title",
        "**Deck Import/Export System:**": "changelog-subsection-title",
        "**Data Storage Optimizations:**": "changelog-subsection-title",
        "**Enhanced TCG Controller (`src/utils/TCGapi/EnhancedTCGController.js`):**": "changelog-subsection-title tech-path",
        "**Custom Card Creator (`src/components/modals/CustomCardCreator.js`):**": "changelog-subsection-title tech-path",
        "**TCG Sim Controller (`src/utils/TCGsim/TCGSimController.js`):**": "changelog-subsection-title tech-path",
    }), []); // Empty dependency array means this object is created once

    const renderChangelogItem = (itemText) => {
        let textToProcess = String(itemText).trimStart(); // Trim leading spaces from the item
        const elements = [];
        let keyIndex = 0;

        // Check for subsection title
        let isSubsection = false; // retained for readability
        for (const titleKeyword in keywordsMap) {
            if (textToProcess.startsWith(titleKeyword) && keywordsMap[titleKeyword].includes('subsection-title')) {
                elements.push(<span key={`sub-${keyIndex++}`} className={keywordsMap[titleKeyword]}>{titleKeyword.replace(/\*\*/g, '')}</span>);
                textToProcess = textToProcess.substring(titleKeyword.length).trimStart();
                isSubsection = true;
                break;
            }
        }
        
        // If it was a subsection title, the rest of the line might be just that title, or it might be followed by a keyword.
        // If it's not a subsection, or if there's text after the subsection title, process for keywords.

        const keywordRegex = new RegExp(`^(${Object.keys(keywordsMap).filter(k => !keywordsMap[k].includes('subsection-title')).map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\s*`, 'i');
        const match = textToProcess.match(keywordRegex);

        if (match) {
            const matchedKeyword = match[1].toUpperCase();
            const keywordClass = keywordsMap[matchedKeyword];
            if (keywordClass) {
                elements.push(<span key={`kw-${keyIndex++}`} className={`changelog-keyword ${keywordClass}`}>{match[1]}</span>);
                textToProcess = textToProcess.substring(match[0].length).trimStart();
            }
        }

        // Process remaining text for bolding
        const parts = textToProcess.split(/(\*\*.*?\*\*)/g);
        parts.forEach((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                elements.push(<strong key={`strong-${keyIndex}-${index}`}>{part.substring(2, part.length - 2)}</strong>);
            } else if (part) { // Ensure part is not an empty string
                elements.push(<span key={`text-${keyIndex}-${index}`}>{part}</span>);
            }
        });
        keyIndex++;
        return elements;
    };

    // Determine the default active key for the Accordion (latest version's index)
    const defaultAccordionKey = useMemo(() => {
        const latestOpenIndex = activeChangelog.findIndex(log => log.isOpen);
        return latestOpenIndex !== -1 ? latestOpenIndex.toString() : "0";
    }, [activeChangelog]);


    return (
        <>
            <NavBar className="app-navbar" bg={darkMode ? "dark" : "light"} data-bs-theme={darkMode ? "dark" : "light"} fixed='top'>
                <div className="navbar-left-slot">
                    <NavBar.Brand
                        href="/"
                        className="navbar-brand-home"
                        aria-label="Pokemon TCG Deck Builder"
                        onClick={(event) => {
                            event.preventDefault();
                            navigate('/');
                        }}
                    >
                        <span className="header-label-full">Pokemon TCG Deck Builder</span>
                        <span className="header-label-short">Deck Builder</span>
                    </NavBar.Brand>
                    <TryNewDeckBanner />
                </div>

                <div className="header-actions">
                        <Button
                            variant={darkMode ? "outline-light" : "outline-primary"}
                            size="sm"
                            className="header-action-btn"
                            onClick={openMetaPTCG}
                            aria-label="Play Meta-PTCG"
                        >
                            <FontAwesomeIcon icon={faGlobe} />
                            <span className="header-label-full">Play Meta-PTCG</span>
                            <span className="header-label-short">Play</span>
                        </Button>

                        <Button
                            variant={darkMode ? "outline-light" : "outline-secondary"}
                            size="sm"
                            className="header-action-btn"
                            onClick={() => navigate('/grid/')}
                        >
                            <FontAwesomeIcon icon={faTh} />
                            Grid
                        </Button>

                        {activeLatest && activeLatest.version && (
                            <Badge
                                bg={catalogMode ? 'danger' : 'primary'}
                                onClick={handleShowChangelog}
                                className={`badge-hover header-version-badge${catalogMode ? ' catalog-version-badge' : ''}`}
                                pill
                            >
                                v{activeLatest.version}
                            </Badge>
                        )}

                        <button
                            type="button"
                            className="header-theme-toggle"
                            onClick={toggleTheme}
                            aria-label={darkMode ? 'Switch to light theme' : 'Switch to dark theme'}
                        >
                            <FontAwesomeIcon icon={darkMode ? farMoon : fasSun} size="lg" />
                        </button>
                </div>
            </NavBar>

            {activeChangelog && activeChangelog.length > 0 && (
                <Modal show={showChangelogModal} onHide={handleCloseChangelog} contentClassName={`${modalThemeClass} changelog-modal`} size='lg' scrollable>
                    <Modal.Header closeButton className="changelog-modal-header">
                        <Modal.Title className="changelog-modal-title">
                            {catalogMode ? 'Catalog Changelog' : 'Application Changelog'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="changelog-modal-body">
                        <Accordion defaultActiveKey={defaultAccordionKey}>
                            {activeChangelog.map((versionLog, index) => (
                                <Accordion.Item eventKey={index.toString()} key={versionLog.version} className={theme === 'dark' ? 'accordion-item-dark' : ''}>
                                    <Accordion.Header>
                                        <span className="fw-bold me-2">Version {versionLog.version}</span> 
                                        {versionLog.title && <span className="text-muted me-2">- {versionLog.title}</span>}
                                        {versionLog.date && <small className="text-muted">({versionLog.date})</small>}
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        {versionLog.changes && versionLog.changes.map((category, categoryIndex) => (
                                            <div key={categoryIndex} className="changelog-category mb-4">
                                                <h5 className="changelog-category-title">{category.title}</h5>
                                                {category.items && category.items.length > 0 ? (
                                                    <ul className="list-unstyled">
                                                        {category.items.map((item, itemIndex) => (
                                                            <li key={itemIndex} className="changelog-item">
                                                                {renderChangelogItem(item)}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p>No specific items listed for this category.</p>
                                                )}
                                            </div>
                                        ))}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                        <hr className="mt-4 mb-3"/>
                        <p className="text-muted small">
                            {catalogMode
                                ? 'Changelog for the catalog Deck Builder.'
                                : 'Changelog for the Meta-PTCG Deck Builder.'}
                        </p>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
}

export default Header;
